/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import type { Field } from "@src/@types/Field";

/*
 * Swift's names for Coriolis' concepts, applied to every label and description the dictionary
 * returns (the providers' option schemas come from the backend with "minion" / "bare metal"
 * wording): minion pool -> Swift Agent, minion -> agent, bare metal (hub) -> on-prem (hub).
 */
const keepCase = (match: string, replacement: string) => {
  if (match === match.toUpperCase() && match !== match.toLowerCase()) {
    return replacement.toUpperCase();
  }
  if (match.charAt(0) === match.charAt(0).toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
};
// Ops sentence case for field labels ("Storage Container" -> "Storage container"). Only plain
// capitalised words after the first are lowered; acronyms, mixed case ("KubeVirt") and product
// names keep their spelling.
const KEEP_CAPITALISED = new Set([
  "SwiftIQ", "Agent", "Agents", "Windows", "Linux", "Nutanix", "Azure", "Proxmox", "Prism", "Microsoft",
  "Kubernetes", "Keystone", "Barbican", "Amazon", "Google", "Ubuntu", "Debian", "Red", "Hat", "Oracle",
  "Tech9IQ", "Hyper", "Power", "Cloud", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday",
  "Saturday", "Sunday", "January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December", "UTC",
]);
export const sentenceCaseLabel = (label: string): string =>
  label
    .split(" ")
    .map((word, i) => {
      if (i === 0) return word;
      const m = /^([(\["']?)([A-Z][a-z]+)([^A-Za-z]*)$/.exec(word);
      if (!m) return word;
      if (KEEP_CAPITALISED.has(m[2])) return word;
      // "Swift Agent": keep the word after Swift as part of the name.
      return `${m[1]}${m[2].toLowerCase()}${m[3]}`;
    })
    .join(" ");

export const swiftTerminology = (text: string): string => {
  if (!text) {
    return text;
  }
  return text
    .replace(/\bminion[ _-]pools\b/gi, "SwiftIQ Agents")
    .replace(/\bminion[ _-]pool\b/gi, "SwiftIQ Agent")
    .replace(/\bminions\b/gi, m => keepCase(m, "agents"))
    .replace(/\bminion\b/gi, m => keepCase(m, "agent"))
    .replace(/\bbare[ -]metal hub\b/gi, m => keepCase(m, "on-prem hub"))
    .replace(/\bmetal hub\b/gi, m => keepCase(m, "on-prem hub"))
    .replace(/\bbare[ -]metal\b/gi, m => keepCase(m, "on-prem"));
};

/**
 * Messages from the migration engine (task progress, errors) as clients see them: the product is Swift.
 * Only the word "Coriolis" changes; names such as "coriolis-replica-x" or "CoriolisException" are left as they are.
 */
export const swiftBrand = (text: string): string => {
  if (!text) {
    return text;
  }
  return text.replace(/\bCoriolis\b/g, "SwiftIQ");
};

// The word will be uppercased
const acronyms = [
  "id",
  "api",
  "url",
  "vm",
  "os",
  "dhcp",
  "sql",
  "oci",
  "aws",
  "vcn",
  "ca",
  "mb",
  "gb",
  "mac",
  "ip",
  "cpu",
];

// The word will be replaced
const abbreviations = {
  migr: "Migration",
  auth: "Authentication",
  fip: "Floating IP",
  vcpus: "vCPUs",
  vcpu: "vCPU",
};

const dictionary = {
  migr_image: "Migration Image Name or Id",
  migr_network: "Migration Network Name or ID",
  migr_worker_boot_from_volume: "Boot Migration Workers from Volume",
  volumes_are_zeroed: "Volumes on destination are created zeroed",
  keep_mac: "Keep MAC address",
  reuse_ports: "Reuse Existing Ports",
  replace_mac: "Replace MAC Address",
  migr_worker_use_config_drive: "Migration Worker use ConfigDrive",
  migr_worker_use_fip: "Migration Worker use FIP",
  separate_vm: "Separate Migration/VM?",
  windows_migr_image: {
    description:
      "The Windows Migration Image information found on the Azure page",
  },
  linux_migr_image: {
    description:
      "The Linux Migration Image information found on the Azure page",
  },
  duplicate_to_project: {
    label: "Project",
    description: "Duplicate endpoint to selected project",
  },
  // AzureStack suffixes
  azure_datalake_analytics_catalog_and_job_endpoint:
    "Azure Datalake Analytics Catalog And Job Endpoint Suffix",
  azure_datalake_store_file_system_endpoint:
    "Azure Datalake Store File System Endpoint Suffix",
  keyvault_dns: "Keyvault DNS Suffix",
  sql_server_hostname: "SQL Server Hostname Suffix",
  storage_endpoint: "Storage Endpoint Suffix",
  preserve_nic_ips: "Preserve NIC IPs",
  openstack_use_current_user:
    "Use Current User/Project/Domain for Authentication",
  windows_os_image: "Windows OS",
  linux_os_image: "Linux OS",
  skip_os_morphing: {
    description: `Whether or not to skip the OSMorphing process.
    This process is recommended when migrating VMs between platforms with different underlying virtualization technologies or initialization agents, as SwiftIQ will adapt the offline OS installation by automatically installing any additional drivers/configurations required by the destination platform.
    This can be safely skipped if the source and destination platforms are identical (e.g: migrating between two separate regions of the same Public Cloud), or if the two platforms share the same virtualization technology.
    (e.g: migrating between two KVM-based OpenStacks from different vendors)`,
  },
  force: {
    description: `Whether or not SwiftIQ should forcibly attempt the Deployment process despite the Transfer not having any successful Executions.
    This is only recommended if it is known that the Transfer disks were successfully synced but some latter cleanup steps failed (e.g: deleting source-side temporary resources).
    This will not help if the Transfer disks were never successfully synced.`,
  },
  clone_disks: {
    description: `Copy the replica's disks into the new VM. The copy keeps the replica for later syncs and test cut-overs, but on large disks it can take a long time, which lengthens the downtime.
    Off (the default): the replica's disks are handed to the new VM in seconds, without a copy, and the next sync of this transfer is a full one.`,
  },
  shutdown_instances: {
    label: "Shut down source VMs",
    description:
      "This option can be used before completing the Deployment on the target cloud. After the source instance(s) shutdown, a last snapshot will be executed, in order to transfer the last bits of data to the target cloud, and the source instance(s) will be left stopped.",
  },
  allow_remote_access: {
    label: "Allow RDP / SSH access",
    description:
      "Open RDP (Windows, TCP 3389) or SSH (Linux, TCP 22) to the migrated VM from the address below: SwiftIQ creates a security group (AWS) or network security group (Azure) for it. Filling in the address turns this on.",
  },
  remote_access_cidr: {
    label: "RDP / SSH allowed from (CIDR)",
    description:
      "Your public address, e.g. 203.0.113.10/32. Empty: the SwiftIQ host's public IP.",
  },
  assign_public_ip: {
    label: "Assign Public IP",
    description: "Give the migrated VM a public IP address (needed for RDP/SSH from the internet).",
  },
  direct_pull: {
    label: "Direct transfer",
    description:
      "Disk data goes straight between the platforms' temporary VMs (e.g. Nutanix to AWS EBS, AWS/Azure to Nutanix) instead of through the SwiftIQ host. Falls back to the SwiftIQ host when the temporary VM cannot reach the other platform.",
  },
  auto_deploy: {
    label: "Auto Deploy",
    description:
      "When enabled, the transfer will automatically deploy the instances on the destination cloud after the transfer is complete.",
  },
};

const cache: {
  name: string;
  label: string | null | undefined;
  description: string | null | undefined;
  key: string;
}[] = [];

class LabelDictionary {
  // Fields which have enums for which dictionary labels should be used.
  // If a field has enums and is not in this array, their values will be used as labels
  static enumFields = [
    "port_reuse_policy",
    "replica_export_mechanism",
    "virtual_disk_clone_type",
  ];

  /**
   *
   * @param {string} fieldName The name of the field
   * @param {string} dictionaryKey Optional key to more uniquely
   * identify the field added to schema cache.
   * The `dictionaryKey` is composed by `${provider}-${direction}.
   * Direction is 'destination' or 'source'.
   */
  static get(
    fieldName: string | null | undefined,
    dictionaryKey?: string,
  ): string {
    if (!fieldName) {
      return "";
    }
    const cachItem = cache.find(
      i => i.key === dictionaryKey && i.name === fieldName,
    );
    if (cachItem && cachItem.label) {
      return sentenceCaseLabel(swiftTerminology(cachItem.label));
    }
    const dict: any = dictionary;
    const labelInfo = dict[fieldName];
    if (labelInfo) {
      if (typeof labelInfo === "string") {
        return sentenceCaseLabel(swiftTerminology(labelInfo));
      }
      if (labelInfo.label) {
        return sentenceCaseLabel(swiftTerminology(labelInfo.label));
      }
    }

    let words = fieldName.split("_");
    words = words.map(word => {
      const acronym = acronyms.find(a => a === word);
      const abb: any = abbreviations;
      const newWord = acronym
        ? acronym.toUpperCase()
        : abb[word] || word.toLowerCase();
      return newWord.charAt(0).toUpperCase() + newWord.substr(1);
    });
    return sentenceCaseLabel(swiftTerminology(words.join(" ")));
  }

  static getDescription(fieldName: string, dictionaryKey?: string): string {
    const cachItem = cache.find(
      i => i.key === dictionaryKey && i.name === fieldName,
    );
    if (cachItem && cachItem.description) {
      return swiftTerminology(cachItem.description);
    }

    const dict: any = dictionary;
    const labelInfo = dict[fieldName];

    if (labelInfo && typeof labelInfo === "object") {
      return swiftTerminology(labelInfo.description || "");
    }

    return "";
  }

  static pushToCache(field: Field, dictionaryKey: string) {
    if (!(field.title || field.description)) {
      return;
    }
    const existingIndex = cache.findIndex(
      i => i.key === dictionaryKey && i.name === field.name,
    );
    const entry = {
      label: field.title,
      description: field.description,
      name: field.name,
      key: dictionaryKey,
    };
    if (existingIndex >= 0) {
      cache[existingIndex] = entry;
    } else {
      cache.push(entry);
    }
  }
}

export default LabelDictionary;
